/**
 * The one-decision dialog, as a promise.
 *
 * One Radix AlertDialog mounted by the provider, drawn as finixt-ui's
 * ConfirmDialog; call sites await `useConfirm()` and keep their own flow.
 * Escape, the scrim, and Cancel answer false; a second question asked while
 * one is open answers the first false. Initial focus follows the tone: on
 * Cancel for danger, where Enter must not go through by reflex, and on the
 * confirm otherwise.
 */
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';

import { Button, Icon } from './finixt.js';
import { useReturnFocus } from './useReturnFocus.js';

const SCRIM_Z_INDEX = 80;
const WIDTH = 420;

export type ConfirmTone = 'default' | 'warn' | 'danger';

export interface ConfirmOptions {
  title: string;
  /** One or two sentences naming exactly what will happen. */
  description: ReactNode;
  confirmLabel?: string | undefined;
  cancelLabel?: string | undefined;
  tone?: ConfirmTone | undefined;
}

export type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

interface Pending extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

const TONES: Record<
  ConfirmTone,
  { icon: string; color: string; tint: string; variant: 'primary' | 'danger' }
> = {
  danger: {
    icon: 'warning',
    color: 'var(--danger)',
    tint: 'var(--danger-tint)',
    variant: 'danger',
  },
  warn: { icon: 'error', color: 'var(--warn)', tint: 'var(--warn-tint)', variant: 'primary' },
  default: {
    icon: 'help',
    color: 'var(--text-soft)',
    tint: 'var(--surface-fill)',
    variant: 'primary',
  },
};

const ConfirmContext = createContext<ConfirmFn | null>(null);

/** Asks the operator a yes/no question and resolves with the answer. */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm() must be used within a ConfirmProvider');
  return ctx;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const pendingRef = useRef<Pending | null>(null);
  const cancelHolder = useRef<HTMLSpanElement | null>(null);
  const confirmHolder = useRef<HTMLSpanElement | null>(null);
  const open = pending !== null;
  const onCloseAutoFocus = useReturnFocus(open);

  const settle = useCallback((value: boolean) => {
    const current = pendingRef.current;
    if (!current) return;
    pendingRef.current = null;
    setPending(null);
    current.resolve(value);
  }, []);

  const confirm = useCallback<ConfirmFn>(
    (options) =>
      new Promise<boolean>((resolve) => {
        pendingRef.current?.resolve(false);
        const next = { ...options, resolve };
        pendingRef.current = next;
        setPending(next);
      }),
    [],
  );

  const tone = TONES[pending?.tone ?? 'default'];
  const danger = pending?.tone === 'danger';

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog.Root open={open} onOpenChange={(next) => !next && settle(false)}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: SCRIM_Z_INDEX,
              background: 'var(--scrim)',
              animation: 'fadein var(--dur-fast) var(--ease-out)',
            }}
          />
          <AlertDialog.Content
            onCloseAutoFocus={onCloseAutoFocus}
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              const holder = danger ? cancelHolder.current : confirmHolder.current;
              holder?.querySelector('button')?.focus();
            }}
            style={{
              position: 'fixed',
              zIndex: SCRIM_Z_INDEX + 1,
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: WIDTH,
              maxWidth: 'calc(100vw - 32px)',
              display: 'flex',
              flexDirection: 'column',
              background: 'var(--surface-card)',
              border: '1px solid var(--border-strong)',
              borderRadius: 'var(--radius-sheet)',
              boxShadow: 'var(--shadow-sheet)',
              outline: 'none',
              animation: 'popin var(--dur-enter) var(--ease-out)',
            }}
          >
            <AlertDialog.Title
              style={{
                margin: 0,
                padding: '16px 18px 0',
                fontSize: 'var(--text-title)',
                fontWeight: 'var(--weight-bold)',
                letterSpacing: 'var(--tracking-title)',
                color: 'var(--text-strong)',
              }}
            >
              {pending?.title}
            </AlertDialog.Title>
            <div style={{ display: 'flex', gap: 11, padding: '14px 18px' }}>
              <span
                style={{
                  flexShrink: 0,
                  width: 32,
                  height: 32,
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: 'var(--radius-round)',
                  background: tone.tint,
                }}
              >
                <Icon name={tone.icon} size={18} color={tone.color} />
              </span>
              <AlertDialog.Description
                style={{
                  margin: 0,
                  minWidth: 0,
                  fontSize: 'var(--text-body-sm)',
                  color: 'var(--text-muted)',
                  lineHeight: 'var(--leading-body)',
                }}
              >
                {pending?.description}
              </AlertDialog.Description>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 7,
                padding: '11px 18px',
                borderTop: '1px solid var(--border-divider)',
              }}
            >
              <span ref={cancelHolder} style={{ display: 'contents' }}>
                <Button variant="ghost" onClick={() => settle(false)}>
                  {pending?.cancelLabel ?? 'Cancel'}
                </Button>
              </span>
              <span ref={confirmHolder} style={{ display: 'contents' }}>
                <Button variant={tone.variant} onClick={() => settle(true)}>
                  {pending?.confirmLabel ?? 'Confirm'}
                </Button>
              </span>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </ConfirmContext.Provider>
  );
}
